import React, { useRef, useEffect } from 'react';
import logoDate from '../../assets/date.svg';
import logoSort from '../../assets/review/sort.svg';
import logoFB from '../../assets/review/logoFB.svg';
import logoGMB from '../../assets/review/logoGMB.svg';
import logoOS from '../../assets/review/logoOS.svg';
import { FilterGroup, FilterOption, FilterSorterProps } from '../type';

const FilterSorter: React.FC<FilterSorterProps> = ({
    onFilterChange,
    onSortChange,
    showFilter,
    showSorter,
    toggleShowFilter,
    toggleShowSorter,
    currentFilters,
    currentSort,
    platformRatings,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                if (showFilter) {
                    toggleShowFilter();
                }
                if (showSorter) {
                    toggleShowSorter();
                }
            }
        };

        if (showFilter || showSorter) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showFilter, showSorter, toggleShowFilter, toggleShowSorter]);

    const filterOptions: FilterGroup[] = [
        {
            id: 'status',
            label: 'Statut',
            options: [
                { value: 'unread', label: 'Non lu' },
                { value: 'read', label: 'Lu/Non répondu' },
                { value: 'answered', label: 'Répondu' },
            ],
        },
        {
            id: 'network',
            label: 'Réseau',
            options: [
                { value: 'google_my_business', label: 'Google My Business', icon: logoGMB },
                { value: 'facebook', label: 'Facebook', icon: logoFB },
                { value: 'opinion_system', label: 'Opinion System', icon: logoOS },
            ],
        },
    ];

    const sortOptions: FilterOption[] = [
        { value: 'date_desc', label: 'Date (Plus récent)' },
        { value: 'date_asc', label: 'Date (Plus ancien)' },
    ];

    return (
        <div ref={containerRef} className="flex items-center justify-end gap-3 relative">
            <div
                onClick={toggleShowFilter}
                className="
                  flex
                  items-center
                  gap-[5px]
                  cursor-pointer
                  px-3
                  py-2
                  rounded-[4px]
                  transition-colors
                  duration-200
                  hover:bg-[rgba(0,0,0,0.05)]
                "
            >
                <img className="w-auto h-[15px]" src={logoSort} alt="logo sort" />
                <span className="text-medium font-medium text-[#333333]">Filtrer par</span>
            </div>

            <div
                onClick={toggleShowSorter}
                className="flex items-center gap-[5px] cursor-pointer px-[12px] py-[8px] rounded-[4px] transition-colors duration-200 hover:bg-black/5"
            >
                <img className="w-auto h-[15px]" src={logoDate} alt="logo date" />
                <span className="font-medium text-dark-gray">Trier par</span>
            </div>

            {showFilter && (
                <div
                    className="filter-sorter-wrapper__content flex flex-col p-[15px] absolute right-0 top-[40px] w-[330px] rounded-[8px] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-[1000]"
                    style={{ animation: 'dropdownFade 0.2s ease' }}
                >
                    <div className="text-super-medium font-medium text-dark-gray mb-[10px]">
                        Filtrer par
                    </div>
                    <div className="w-full border-t border-light-gray mx-auto mb-[12px]" />

                    {filterOptions.map(filterGroup => (
                        <div key={filterGroup.id} className="">
                            <div className="text-medium font-medium text-dark-gray mb-3">
                                {filterGroup.label}
                            </div>
                            <div className="flex flex-col">
                                {filterGroup.id === 'network'
                                    ? filterGroup.options
                                          .filter(option =>
                                              platformRatings.find(
                                                  rating =>
                                                      rating.platform === option.value &&
                                                      rating.totalReviews > 0,
                                              ),
                                          )
                                          .map(option => (
                                              <label
                                                  key={option.value}
                                                  className="flex items-center gap-1.5 cursor-pointer transition-all duration-200 ease-linear hover:bg-black/5 text-medium text-gray"
                                              >
                                                  <input
                                                      type="checkbox"
                                                      checked={currentFilters[
                                                          filterGroup.id
                                                      ]?.includes(option.value)}
                                                      onChange={() =>
                                                          onFilterChange(
                                                              filterGroup.id,
                                                              option.value,
                                                          )
                                                      }
                                                  />
                                                  {'icon' in option && (
                                                      <img
                                                          src={option.icon}
                                                          alt=""
                                                          className="w-[15px]"
                                                      />
                                                  )}
                                                  <span>{option.label}</span>
                                              </label>
                                          ))
                                    : filterGroup.options.map(option => (
                                          <label
                                              key={option.value}
                                              className="flex items-center gap-1.5 cursor-pointer transition-all duration-200 ease-linear hover:bg-black/5 text-medium text-gray"
                                          >
                                              <input
                                                  type="checkbox"
                                                  checked={currentFilters[filterGroup.id]?.includes(
                                                      option.value,
                                                  )}
                                                  onChange={() =>
                                                      onFilterChange(filterGroup.id, option.value)
                                                  }
                                              />
                                              <span>{option.label}</span>
                                          </label>
                                      ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showSorter && (
                <div
                    className="filter-sorter-wrapper__content flex flex-col p-[15px] absolute right-0 top-[40px] w-[330px] rounded-[8px] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-[1000]"
                    style={{ animation: 'dropdownFade 0.2s ease' }}
                >
                    <div className="text-super-medium font-medium text-dark-gray mb-[10px]">
                        Trier par
                    </div>
                    <div className="w-full border-t border-light-gray mx-auto pt-1.5" />

                    <div className="flex flex-col">
                        {sortOptions.map(option => (
                            <label
                                key={option.value}
                                className="flex items-center gap-1.25 cursor-pointer transition-all duration-200 ease-in-out"
                            >
                                <input
                                    type="radio"
                                    name="sort"
                                    checked={currentSort === option.value}
                                    onChange={() => onSortChange(option.value)}
                                />
                                <span className="text-medium text-gray flex-grow ">
                                    {option.label}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterSorter;
